import { useEffect, useState } from 'react'
import { useAppStore } from '@/stores/appStore'
import { api, PublicDropLink } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import { Copy, RefreshCw, User } from 'lucide-react'

export function ProfilePage() {
  const { user, fetchUser } = useAppStore()
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [isSaving, setIsSaving] = useState(false)
  const [publicDropLinks, setPublicDropLinks] = useState<PublicDropLink[]>([])
  const [newLinkLabel, setNewLinkLabel] = useState('')
  const [isCreatingLink, setIsCreatingLink] = useState(false)

  const loadPublicDropLinks = async () => {
    try {
      const { links } = await api.me.publicDrop.list()
      setPublicDropLinks(links || [])
    } catch {
      toast({ title: 'Failed to load public drop links', variant: 'destructive' })
    }
  }

  useEffect(() => {
    if (!user) return
    loadPublicDropLinks()
  }, [user])

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true)
      await api.me.update({ display_name: displayName })
      toast({ title: 'Profile updated successfully' })
      await fetchUser()
    } catch {
      toast({ title: 'Failed to update profile', variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreatePublicDropLink = async () => {
    try {
      setIsCreatingLink(true)
      await api.me.publicDrop.create({ label: newLinkLabel.trim() || undefined })
      setNewLinkLabel('')
      await loadPublicDropLinks()
      toast({ title: 'Public drop link created' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to create link', variant: 'destructive' })
    } finally {
      setIsCreatingLink(false)
    }
  }

  const handleTogglePublicDropLink = async (link: PublicDropLink) => {
    try {
      await api.me.publicDrop.update(link.id, { enabled: !link.enabled })
      await loadPublicDropLinks()
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to update link', variant: 'destructive' })
    }
  }

  const handleRegeneratePublicDropLink = async (link: PublicDropLink) => {
    try {
      await api.me.publicDrop.regenerate(link.id)
      await loadPublicDropLinks()
      toast({ title: 'Public drop link regenerated' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to regenerate link', variant: 'destructive' })
    }
  }

  const copyPublicDropLink = async (link: PublicDropLink) => {
    const url = `${window.location.origin}/drop/${link.token}`
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: 'Public drop link copied' })
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' })
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Profile</h1>

      <div className="space-y-8">
        <section className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Personal Details</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input value={user?.email || ''} disabled />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Display Name</label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </section>

        <section className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Copy className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Public Drop Links</h2>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Share these links with external people so they can upload files directly to your public drop box.
            Uploaded files appear in Shared with Me and in My Files → My Public Drop Box.
          </p>

          <div className="flex gap-2 mb-4">
            <Input
              value={newLinkLabel}
              onChange={(e) => setNewLinkLabel(e.target.value)}
              placeholder="Optional label (e.g. Steve)"
            />
            <Button onClick={handleCreatePublicDropLink} disabled={isCreatingLink}>
              {isCreatingLink ? 'Creating...' : 'Create Link'}
            </Button>
          </div>

          <div className="space-y-2">
            {publicDropLinks.map((link) => (
              <div key={link.id} className="border rounded-md p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{link.label || 'Unnamed Link'}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {window.location.origin}/drop/{link.token}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyPublicDropLink(link)}>
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleRegeneratePublicDropLink(link)}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Regenerate
                  </Button>
                  <Button
                    variant={link.enabled ? 'default' : 'secondary'}
                    size="sm"
                    onClick={() => handleTogglePublicDropLink(link)}
                  >
                    {link.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
              </div>
            ))}
            {publicDropLinks.length === 0 && (
              <p className="text-sm text-muted-foreground">No links yet.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
